import type {
  ContractConsumption,
  ContractInput,
  ContractResponse,
  CostRateResolver,
  PlanRollup,
  ProjectResponse,
  ProjectSummaryResponse,
  RealizedByNode,
  SellRateResolver,
} from '@timelog/pm-shared';
import {
  calculateConsumption,
  createCostRateResolver,
  createSellRateResolver,
  planCalcOptionsOf,
  rollupPlan,
} from '@timelog/pm-shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import type { Dispatch, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { createInitialEditorState, editorReducer } from './editorStore';
import type { EditorAction, EditorState } from './editorStore';
import { flattenPlan } from './flatten';
import type { FlattenResult } from './flatten';
import { createPlanNode } from './nodeFactory';
import { useAutosave } from './useAutosave';

import { useBudgetModel } from '@/analytics';
import { todayIso } from '@/lib';

/**
 * Owns the editor state and every derived number. The rollup runs the shared
 * CALC-1..8 pass over the LOCAL document (instant feedback); realized hours
 * are the one thing only the server knows, merged in from the summary query.
 */
export type PlanEditorActions = {
  /**
   * Adds a task near the given node: a top-level row gets it as its last
   * subtask, a nested row gets it as its next sibling. With no anchor it
   * appends a new top-level task.
   */
  addTask: (anchorId?: string | null) => void;
  /** Empty-state CTA: first top-level task, title focused. */
  startPlanning: () => void;
  /** Immediate save attempt (used by tests and explicit flows). */
  flushSave: () => Promise<void>;
};

export type PlanEditorContextValue = {
  state: EditorState;
  dispatch: Dispatch<EditorAction>;
  project: ProjectResponse;
  contractInputs: ContractInput[];
  flat: FlattenResult;
  rollup: PlanRollup;
  consumption: ContractConsumption[];
  /** CALC-2/CALC-23 sell-rate resolution by contract type (D15); `.state` says whether it priced at all. */
  resolveRate: SellRateResolver;
  /** CALC-21 cost-rate resolution per staffing line (D14, ADR 0042) — what a line costs, not what it bills. */
  resolveCost: CostRateResolver;
  /** The evaluation day time-phased cost rates resolve against (D14) — fixed for the editor session. */
  today: string;
  realizedByNode: RealizedByNode;
  actions: PlanEditorActions;
};

const PlanEditorContext = createContext<PlanEditorContextValue | undefined>(undefined);

export type PlanEditorProviderProps = {
  project: ProjectResponse;
  contracts: ContractResponse[];
  summary: ProjectSummaryResponse | undefined;
  children: ReactNode;
};

export const PlanEditorProvider = ({
  project,
  contracts,
  summary,
  children,
}: PlanEditorProviderProps) => {
  const [state, dispatch] = useReducer(
    editorReducer,
    { projectId: project.id, document: project.document, version: project.version },
    createInitialEditorState,
  );

  // Re-key the editor when a different project loads. Server refetches of the
  // SAME project never overwrite local edits implicitly (autosave adopts).
  useEffect(() => {
    if (state.projectId !== project.id) {
      dispatch({
        type: 'RESET_FROM_SERVER',
        projectId: project.id,
        document: project.document,
        version: project.version,
      });
    }
  }, [project.id, project.document, project.version, state.projectId]);

  const autosave = useAutosave(state, dispatch);
  const flush = autosave.flush;

  const contractInputs = useMemo<ContractInput[]>(
    () =>
      contracts.map((contract) => ({
        id: contract.id,
        name: contract.name,
        type: contract.type,
        document: contract.document,
      })),
    [contracts],
  );

  /*
    Which contract a new task is stamped with (ADR 0033 — a contract is an
    explicit mapping with no read-time fallback, so an unmapped task has no
    contract and no price at all).

    ONE contract on the project is the only case that prefills, because it is
    the only one with a single honest answer: there is nothing else the task
    could bill through, and making someone point every new task at the only
    contract there was is what the pilot tripped over.

    Deliberately NOT prefilling from `defaultContractId` when the project holds
    several contracts, even though ADR 0033's decision calls it "a creation-time
    prefill". `planning-contract-mapping.spec.ts` asserts the opposite in as many
    words — "a new top-level task starts unmapped (contract choice is the
    user's)" — and with several contracts to choose from that is the better
    behaviour. The ADR and that test disagree; this narrows to the half they
    agree on rather than silently picking a winner.
  */
  const contractPrefillId = contractInputs.length === 1 ? contractInputs[0]!.id : undefined;

  useEffect(() => {
    dispatch({ type: 'SET_CONTRACT_PREFILL', contractId: contractPrefillId });
  }, [contractPrefillId]);

  const realizedByNode = useMemo<RealizedByNode>(() => {
    if (!summary) return {};
    return Object.fromEntries(
      Object.entries(summary.plan.nodes).map(([nodeId, rollup]) => [nodeId, rollup.realized]),
    );
  }, [summary]);

  const flat = useMemo(
    () => flattenPlan(state.document.plan, state.foldedIds),
    [state.document.plan, state.foldedIds],
  );

  // One evaluation day per editor session (D14): a plan does not reprice
  // mid-edit because midnight passed; reopening reads the new day.
  const [today] = useState(todayIso);
  // The Completion Principle rides the budget model (BR-30, ADR 0041), so a
  // variant flip reprices fixed-price staffing on reload, like every other setting.
  const budgetModel = useBudgetModel();
  const calcOptions = useMemo(
    () => planCalcOptionsOf(budgetModel.config, today),
    [budgetModel.config, today],
  );

  const rollup = useMemo(
    () => rollupPlan(state.document, contractInputs, realizedByNode, calcOptions),
    [state.document, contractInputs, realizedByNode, calcOptions],
  );

  const consumption = useMemo(
    () => calculateConsumption(contractInputs, state.document, rollup, calcOptions),
    [contractInputs, state.document, rollup, calcOptions],
  );

  const resolveRate = useMemo(
    () => createSellRateResolver(contractInputs, state.document.blendedCostRate, calcOptions),
    [contractInputs, state.document.blendedCostRate, calcOptions],
  );

  const resolveCost = useMemo(
    () => createCostRateResolver(contractInputs, state.document.blendedCostRate, calcOptions),
    [contractInputs, state.document.blendedCostRate, calcOptions],
  );

  /**
   * Appends a top-level task and focuses its title.
   *
   * The node is created with `kind: 'deliverable'` because BR-2 requires it —
   * `ProjectDocumentSchema` rejects a root node of any other kind, so a
   * root-level task would 400 on save. The UI never says the word; this is the
   * only place the internal kind is chosen for a root row.
   */
  /*
    A new row's default name is stored in the document, so it is written in the
    UI language (Web's own `projectPlan.NewTaskName`).
  */
  const { t } = useTranslation('projectManagement');
  const newTaskName = t('projectPlan.NewTaskName');

  const addTopLevelTask = useCallback(() => {
    dispatch({
      type: 'INSERT_NODE',
      node: createPlanNode('deliverable', newTaskName),
      position: { parentId: null, childIndex: Number.MAX_SAFE_INTEGER },
      focusTitle: true,
    });
  }, [newTaskName]);

  const addTask = useCallback(
    (anchorId?: string | null) => {
      const anchor = anchorId ? flat.index.nodeById.get(anchorId) : undefined;

      if (anchor && anchor.kind === 'deliverable') {
        dispatch({
          type: 'INSERT_NODE',
          node: createPlanNode('task', newTaskName),
          position: { parentId: anchor.id, childIndex: Number.MAX_SAFE_INTEGER },
          asChildOf: anchor.id,
          focusTitle: true,
        });
        return;
      }

      if (anchor) {
        const parentId = flat.index.parentById.get(anchor.id) ?? null;
        if (parentId !== null) {
          const parent = flat.index.nodeById.get(parentId)!;
          const index = parent.children.findIndex((child) => child.id === anchor.id);
          dispatch({
            type: 'INSERT_NODE',
            node: createPlanNode('task', newTaskName),
            position: { parentId, childIndex: index + 1 },
            focusTitle: true,
          });
          return;
        }
      }

      // No usable anchor: a new top-level task. This is the replacement for the
      // removed "Add deliverable" button — without it there would be no way to
      // start a second branch of the plan.
      addTopLevelTask();
    },
    [addTopLevelTask, flat.index, newTaskName],
  );

  const actions = useMemo<PlanEditorActions>(
    () => ({
      addTask,
      startPlanning: addTopLevelTask,
      flushSave: flush,
    }),
    [addTopLevelTask, addTask, flush],
  );

  const value = useMemo(
    () => ({
      state,
      dispatch,
      project,
      contractInputs,
      flat,
      rollup,
      consumption,
      resolveRate,
      resolveCost,
      today,
      realizedByNode,
      actions,
    }),
    [
      state,
      project,
      contractInputs,
      flat,
      rollup,
      consumption,
      resolveRate,
      resolveCost,
      today,
      realizedByNode,
      actions,
    ],
  );

  return <PlanEditorContext.Provider value={value}>{children}</PlanEditorContext.Provider>;
};

export const usePlanEditor = (): PlanEditorContextValue => {
  const context = useContext(PlanEditorContext);
  if (!context) {
    throw new Error('usePlanEditor must be used inside a PlanEditorProvider');
  }
  return context;
};
